import { createTheme, alpha } from '@mui/material/styles'
import type {} from '@mui/x-data-grid/themeAugmentation'

// ─── Palette augmentation ────────────────────────────────────────────────────
declare module '@mui/material/styles' {
  interface Palette {
    terra: { main: string; dark: string; light: string }
    gold:  { main: string }
    surface: {
      darkest:  string
      dark:     string
      midGreen: string
      card:     string
      cream:    string
      deepBlue: string
      muted:    string
    }
  }
  interface PaletteOptions {
    terra?:   { main?: string; dark?: string; light?: string }
    gold?:    { main?: string }
    surface?: {
      darkest?:  string
      dark?:     string
      midGreen?: string
      card?:     string
      cream?:    string
      deepBlue?: string
      muted?:    string
    }
  }
  interface SimplePaletteColorOptions {
    [shade: string]: string | undefined
  }
}

// ─── Raw tokens (reuse in sx / CSS vars) ──────────────────────────────────────
export const tokens = {
  green: {
    50:   '#EAF3EC',
    100:  '#CBE3D1',
    200:  '#9FC9AC',
    300:  '#4C8F3A',  // palette primary.light
    400:  '#2F7A3E',
    main: '#145E2B',  // palette primary.main
    600:  '#125327',
    700:  '#104A22',
    800:  '#0D3E1C',  // palette primary.dark
    dark: '#0D3E1C',
    900:  '#082913',
  },
  blue: {
    50:   '#EBF1F6',
    100:  '#D2E0EC',
    200:  '#AFC8DD',
    light: '#6A98C0',  // palette secondary.light
    400:  '#5A8AB5',
    main: '#4B79A1',   // palette secondary.main
    600:  '#3F6B91',
    700:  '#355C7F',
    dark: '#2F5F86',   // palette secondary.dark
    900:  '#1F3F57',
  },
  cream:    '#FAF8F3',  // palette background.default
  textDark: '#1F2937',  // palette text.primary
  textMuted:'#6B7280',  // palette text.secondary
} as const

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      ...tokens.green,
      main:         tokens.green.main,
      light:        tokens.green[300],
      dark:         tokens.green.dark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      ...tokens.blue,
      main:         tokens.blue.main,
      light:        tokens.blue.light,
      dark:         tokens.blue.dark,
      contrastText: '#FFFFFF',
    },
    background: { default: tokens.cream, paper: '#FFFFFF' },
    text:       { primary: tokens.textDark, secondary: tokens.textMuted },
    success:    { main: '#4C8F3A' },
    warning:    { main: '#C8A97E' },
    error:      { main: '#C62828' },
    info:       { main: '#4B79A1' },
    // ── Extended surface tokens ──────────────────────────────────────────
    terra: {
      main:  tokens.blue.main,
      dark:  tokens.blue.dark,
      light: tokens.blue.light,
    },
    gold: { main: tokens.green[300] },
    surface: {
      darkest:  tokens.green[900],
      dark:     tokens.green.dark,
      midGreen: tokens.green.main,
      card:     tokens.green[50],
      cream:    tokens.cream,
      deepBlue: tokens.blue.dark,
      muted:    tokens.green[700],
    },
  },

  // ─── Typography ─────────────────────────────────────────────────────────────
  typography: {
    fontFamily: '"Poppins", system-ui, sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.05 },
    h2: { fontWeight: 600, letterSpacing: '-0.02em',  lineHeight: 1.15 },
    h3: { fontWeight: 600, letterSpacing: '-0.015em'                   },
    h4: { fontWeight: 600                                              },
    h5: { fontWeight: 600, letterSpacing: '-0.01em'                    },
    h6: { fontWeight: 600                                              },
    body1:   { fontSize: '1.05rem', lineHeight: 1.7, fontWeight: 400 },
    body2:   { fontSize: '0.9rem',  lineHeight: 1.6, fontWeight: 400 },
    caption: { fontSize: '0.75rem', letterSpacing: '0.06em', fontWeight: 500 },
    overline:{ fontSize: '0.72rem', letterSpacing: '0.16em', fontWeight: 600 },
    button:  { fontWeight: 600, letterSpacing: '0.02em' },
  },

  shape: { borderRadius: 12 },

  // ─── Component overrides ─────────────────────────────────────────────────────
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.95rem',
          borderRadius: 10,
          padding: '10px 28px',
          transition: 'all 0.22s ease',
        },
        contained: {
          '&.MuiButton-colorPrimary': {
            background: tokens.green.main,
            color: '#FFFFFF',
            '&:hover': {
              background: tokens.green.dark,
              transform: 'translateY(-1px)',
              boxShadow: `0 6px 24px ${alpha(tokens.green.main, 0.35)}`,
            },
          },
          '&.MuiButton-colorSecondary': {
            background: tokens.blue.main,
            color: '#FFFFFF',
            '&:hover': {
              background: tokens.blue.dark,
              transform: 'translateY(-1px)',
              boxShadow: `0 6px 24px ${alpha(tokens.blue.main, 0.4)}`,
            },
          },
        },
        outlined: {
          '&.MuiButton-colorPrimary': {
            borderColor: tokens.green.main,
            borderWidth: 2,
            color: tokens.green.main,
            '&:hover': {
              borderWidth: 2,
              background: alpha(tokens.green.main, 0.06),
              transform: 'translateY(-1px)',
            },
          },
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          background: alpha(tokens.cream, 0.92),
          backdropFilter: 'blur(16px)',
          boxShadow: 'none',
          border: 'none',
          color: tokens.green.dark,
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", system-ui, sans-serif',
          fontSize: '0.72rem',
          fontWeight: 500,
          letterSpacing: '0.04em',
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          background: '#FFFFFF',
          border: `1px solid ${alpha(tokens.green.main, 0.1)}`,
          boxShadow: 'none',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 20px 60px ${alpha(tokens.green.main, 0.12)}`,
            borderColor: alpha(tokens.blue.main, 0.3),
          },
        },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${alpha(tokens.green.main, 0.12)}`,
          borderRadius: 12,
          fontFamily: '"Poppins", system-ui, sans-serif',
          fontSize: '0.9rem',
          background: '#FFFFFF',
          '& .MuiDataGrid-columnHeaders': {
            background: tokens.green[50],
            borderBottom: `1px solid ${alpha(tokens.green.main, 0.18)}`,
            fontFamily: '"Poppins", system-ui, sans-serif',
            fontSize: '0.72rem',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: tokens.green.dark,
          },
          '& .MuiDataGrid-row:hover': {
            background: alpha(tokens.green.main, 0.04),
          },
          '& .MuiDataGrid-cell': {
            borderColor: alpha(tokens.green.main, 0.08),
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${alpha(tokens.green.main, 0.12)}`,
            background: tokens.green[50],
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: '"Poppins", system-ui, sans-serif',
          fontSize: '0.72rem',
          letterSpacing: '0.03em',
          background: tokens.green.dark,
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: { borderColor: alpha(tokens.green.main, 0.12) },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        outlined: {
          border: `1px solid ${alpha(tokens.green.main, 0.12)}`,
        },
      },
    },
  },
})

export default theme
